<template>
    <div class="participant-detailed-info">
        <!-- 3-Tab structure with glider. SFB-1261: the first tab shows the
             participant's latest ranking positions instead of the cumulative
             PnL chart. It is rendered even without trades, since ranking data
             comes from the leaderboard row, not from the analytics call. -->
        <div class="tabs" v-if="hasRanking || trades.length">
            <input
                type="radio"
                :id="`tab-ranking-${uniqueId}`"
                value="ranking"
                v-model="activeTab" />
            <label
                class="tab"
                :for="`tab-ranking-${uniqueId}`">
                <i class="fa fa-regular fa-ranking-star"></i>
                <span class="tab-label">{{ $t('latestRankingPositions') || 'Ranking positions' }}</span>
            </label>

            <input
                type="radio"
                :id="`tab-latestTrades-${uniqueId}`"
                value="latestTrades"
                v-model="activeTab" />
            <label
                class="tab"
                :for="`tab-latestTrades-${uniqueId}`">
                <i class="fa fa-regular fa-list"></i>
                <span class="tab-label">{{ $t('seeLatestTrades') || 'Latest Trades' }}</span>
            </label>

            <input
                type="radio"
                :id="`tab-lotsByAssets-${uniqueId}`"
                value="lotsByAssets"
                v-model="activeTab" />
            <label
                class="tab"
                :for="`tab-lotsByAssets-${uniqueId}`">
                <i class="fa fa-regular fa-chart-pie"></i>
                <span class="tab-label">{{ $t('lotsByAssets') || 'Lots by Assets' }}</span>
            </label>

            <span class="glider" :style="gliderStyle"></span>
        </div>

        <transition name="slide-fade" mode="out-in">
            <!-- Tab 1: Latest ranking positions (SFB-1261) -->
            <div v-if="activeTab === 'ranking'" key="ranking" class="ranking-content">
                <template v-if="hasRanking">
                    <div class="ranking-cards">
                        <!-- Current position -->
                        <div :class="['ranking-card', 'ranking-card--current', rankClass]">
                            <p class="ranking-card__label">{{ $t('currentPosition') || 'Current position' }}</p>
                            <div class="ranking-card__value">
                                <img v-if="medalSrc" :src="medalSrc" class="ranking-card__medal" alt="" />
                                <span class="ranking-card__number">{{ rank }}</span>
                            </div>
                            <p v-if="totalParticipants" class="ranking-card__hint">
                                {{ $t('ofParticipants', { total: totalParticipants }) || `of ${totalParticipants} participants` }}
                            </p>
                        </div>

                        <!-- Previous position (last daily ranking snapshot) -->
                        <div class="ranking-card">
                            <p class="ranking-card__label">{{ $t('previousPosition') || 'Previous position' }}</p>
                            <div class="ranking-card__value">
                                <span v-if="hasPreviousPlace" class="ranking-card__number">{{ previousPlace }}</span>
                                <span v-else class="ranking-card__number ranking-card__number--empty">—</span>
                            </div>
                            <p class="ranking-card__hint">
                                {{ hasPreviousPlace ? ($t('lastRankingUpdate') || 'Last ranking update') : ($t('noPreviousRanking') || 'No previous ranking yet') }}
                            </p>
                        </div>

                        <!-- Movement -->
                        <div :class="['ranking-card', 'ranking-card--delta', deltaClass]">
                            <p class="ranking-card__label">{{ $t('positionChange') || 'Change' }}</p>
                            <div class="ranking-card__value">
                                <template v-if="hasPreviousPlace && positionDelta !== 0">
                                    <i :class="positionDelta > 0 ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'"></i>
                                    <span class="ranking-card__number">{{ Math.abs(positionDelta) }}</span>
                                </template>
                                <span v-else class="ranking-card__number ranking-card__number--empty">—</span>
                            </div>
                            <p class="ranking-card__hint">{{ deltaLabel }}</p>
                        </div>

                        <!-- Score, in the tournament's ranking metric -->
                        <div v-if="showScore && formattedScore" class="ranking-card">
                            <p class="ranking-card__label">{{ scoreLabel || $t('score') || 'Score' }}</p>
                            <div class="ranking-card__value">
                                <span class="ranking-card__number ranking-card__number--score">{{ formattedScore }}</span>
                            </div>
                        </div>
                    </div>

                    <p class="participant-data-disclaimer">{{ $t('rankingSnapshotHint') || 'The previous position comes from the last daily ranking update; the current position is live.' }}</p>
                </template>
                <h4 v-else class="no-content-txt">{{ $t('rankingNotAvailable') || 'Ranking not available yet' }}</h4>
            </div>

            <!-- Tab 2: Latest Trades Table -->
            <div v-else-if="activeTab === 'latestTrades' && trades.length" key="latest-trades" class="trades-content">
                <div class="indicators">
                    <div class="indicator" v-for="(indicator, index) in indicators" :key="index">
                        <p class="indicator-name">{{ $t(indicator.name) }}</p>
                        <p class="indicator-value">{{ indicator.value }}</p>
                    </div>
                </div>
                <table v-if="trades.length >= 3">
                    <thead>
                        <tr>
                            <th>{{ $t('side') }}</th>
                            <th>{{ $t('asset') }}</th>
                            <th>{{ $t('openTime') }}</th>
                            <th>{{ $t('openPrice') }}</th>
                            <th>{{ $t('closeTime') }}</th>
                            <th>{{ $t('closePrice') }}</th>
                            <th class="last-header">{{ $t('netProfit') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(trade, index) in trades" :key="index">
                            <td>{{ trade.side }}</td>
                            <td>{{ trade.asset }}</td>
                            <td>{{ formatDate(trade.openTime) }}</td>
                            <td>{{ trade.entryPrice }}</td>
                            <td>{{ formatDate(trade.closeTime) }}</td>
                            <td>{{ trade.closePrice }}</td>
                            <td>{{ trade.profit }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Tab 3: Lots by Assets Table -->
            <div v-else-if="activeTab === 'lotsByAssets' && trades.length" key="lots-by-assets" class="lots-by-asset-content">
                <table>
                    <thead>
                        <tr>
                            <th class="first-header">{{ $t('asset') }}</th>
                            <th class="last-header">{{ $t('totalLots') || 'Total Lots' }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="lotsByAsset.length === 0">
                            <td colspan="2" class="no-data-message">
                                {{ $t('noDataAvailable') || 'No data available' }}
                            </td>
                        </tr>
                        <tr v-for="(lot, index) in lotsByAsset" :key="index">
                            <td>{{ lot.assetName }}</td>
                            <td>{{ (lot.totalLots || 0).toFixed(2) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <h4 v-else class="no-content-txt">{{ $t('notEnoughTrades') }}</h4>
        </transition>
    </div>
</template>

<script>
export default {
    name: 'ParticipantDetailedInfo',
    props: {
        uniqueId: {
            type: String,
            required: true,
            default: 'default'
        },
        // SFB-1261 — ranking data comes from the leaderboard row the table
        // already has; nothing extra is fetched for it.
        participant: {
            type: Object,
            default: null
        },
        /** 1-based display rank of the participant. */
        rank: {
            type: Number,
            default: null
        },
        totalParticipants: {
            type: Number,
            default: 0
        },
        /** Ranking metric header (e.g. "Profit %"), as shown in the table. */
        scoreLabel: {
            type: String,
            default: ''
        },
        formattedScore: {
            type: String,
            default: ''
        },
        showScore: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            activeTab: 'ranking', // Default to the ranking positions tab (SFB-1261)
            chartSeries: [
                {
                    name: 'Profit',
                    data: [],
                },
            ],
            latestProfitsData: [],
            indicators: [
                { name: 'winRate', value: '0' },
                { name: 'riskReward', value: '0' },
                { name: 'totalTrades', value: '0' },
            ],
            trades: [],
            lotsByAsset: [],
        };
    },
    computed: {
        isDarkTheme() {
            return this.$vuetify.theme.dark;
        },
        chartOptions() {
            return {
                chart: {
                    id: 'basic-line',
                    width: '100%',
                    toolbar: {
                        show: false
                    }
                },
                theme: {
                    mode: this.isDarkTheme ? 'dark' : 'light'
                },
                tooltip: {
                    theme: this.isDarkTheme ? 'dark' : 'light',
                    style: {
                        fontSize: '12px',
                        fontFamily: 'Helvetica, Arial, sans-serif'
                    },
                    x: {
                        show: true
                    },
                    y: {
                        formatter: function(val) {
                            return val.toFixed(2);
                        }
                    }
                },
                xaxis: {
                    categories: [],
                    axisBorder: {
                        show: false
                    },
                    labels: {
                        show: false,
                        style: {
                            colors: this.isDarkTheme ? '#ffffff' : '#333333'
                        }
                    }
                },
                yaxis: {
                    labels: {
                        style: {
                            colors: this.isDarkTheme ? '#ffffff' : '#333333'
                        }
                    }
                },
                colors: ['#9A9A9A'],
                grid: {
                    show: false,
                },
                markers: {
                    size: 5,
                    colors: ['#9A9A9A'],
                    strokeColors: this.isDarkTheme ? '#333333' : '#fff',
                    strokeWidth: 2,
                    hover: {
                        size: 7,
                    },
                },
            };
        },
        organizerColor() {
            if (this.$store.state.tournament?.tournamentSettings?.colors?.primary) {
                const color = this.$store.state.tournament.tournamentSettings.colors.primary;
                return color.startsWith('#') ? color : `#${color}`;
            }
            return '#009F63'; // Default green
        },
        organizerColorStyle() {
            if (this.$store.state.tournament &&
                this.$store.state.tournament.tournamentSettings &&
                this.$store.state.tournament.tournamentSettings.colors &&
                this.$store.state.tournament.tournamentSettings.colors.primary) {
                const color = this.$store.state.tournament.tournamentSettings.colors.primary;
                // Remove # if present and re-add it
                const cleanColor = color.replace('#', '');
                const fullColor = `#${cleanColor}`;
                return {
                    backgroundColor: fullColor,
                    borderColor: fullColor,
                    color: this.getContrastYIQ(cleanColor) === 'black' ? '#000' : '#fff'
                };
            }
            return {};
        },
        gliderStyle() {
            const tabOrder = ['ranking', 'latestTrades', 'lotsByAssets'];
            const tabIndex = tabOrder.indexOf(this.activeTab);
            return {
                transform: `translateX(${tabIndex * 100}%)`,
                backgroundColor: this.organizerColor
            };
        },
        toggleColorStyle() {
            if (this.$store.state.tournament && 
                this.$store.state.tournament.tournamentSettings &&
                this.$store.state.tournament.tournamentSettings.colors &&
                this.$store.state.tournament.tournamentSettings.colors.primary) {
                const color = this.$store.state.tournament.tournamentSettings.colors.primary;
                // Remove # if present and re-add it
                const cleanColor = color.replace('#', '');
                const fullColor = `#${cleanColor}`;
                return {
                    backgroundColor: fullColor
                };
            }
            return {
                backgroundColor: 'var(--v-mainGreen-base)'
            };
        },
        // ---- SFB-1261: latest ranking positions ----
        hasRanking() {
            return Number.isInteger(this.rank) && this.rank > 0 && !!this.participant && !this.participant.isMock;
        },
        previousPlace() {
            const p = this.participant ? this.participant.previousPlace : null;
            return Number.isInteger(p) && p > 0 ? p : null;
        },
        hasPreviousPlace() {
            return this.previousPlace !== null;
        },
        /** Positive = climbed places since the last snapshot, negative = dropped. */
        positionDelta() {
            if (!this.hasRanking || !this.hasPreviousPlace) return 0;
            return this.previousPlace - this.rank;
        },
        deltaClass() {
            if (!this.hasPreviousPlace || this.positionDelta === 0) return 'ranking-card--flat';
            return this.positionDelta > 0 ? 'ranking-card--up' : 'ranking-card--down';
        },
        deltaLabel() {
            if (!this.hasPreviousPlace) return this.$t('noPreviousRanking') || 'No previous ranking yet';
            if (this.positionDelta === 0) return this.$t('noPositionChange') || 'No change';
            const n = Math.abs(this.positionDelta);
            return this.positionDelta > 0
                ? (this.$t('movedUpPositions', { n }) || `Up ${n}`)
                : (this.$t('movedDownPositions', { n }) || `Down ${n}`);
        },
        rankClass() {
            switch (this.rank) {
                case 1: return 'gold';
                case 2: return 'silver';
                case 3: return 'bronze';
                default: return 'normal';
            }
        },
        medalSrc() {
            switch (this.rank) {
                case 1: return require('@/assets/tournament-facelift/medal-gold.svg');
                case 2: return require('@/assets/tournament-facelift/medal-silver.svg');
                case 3: return require('@/assets/tournament-facelift/medal-bronze.svg');
                default: return null;
            }
        },
        // Latest Trades Bar Chart Series
        latestTradesChartSeries() {
            return [{
                name: this.$t('tradeProfit') || 'Trade Profit',
                data: this.latestProfitsData.map(item => item.profit || 0)
            }];
        },
        // Latest Trades Bar Chart Options
        latestTradesChartOptions() {
            return {
                chart: {
                    id: 'latest-trades',
                    type: 'bar',
                    width: '100%',
                    toolbar: {
                        show: false
                    },
                    animations: {
                        enabled: true,
                        easing: 'easeinout',
                        speed: 800
                    }
                },
                theme: {
                    mode: this.isDarkTheme ? 'dark' : 'light'
                },
                plotOptions: {
                    bar: {
                        borderRadius: 6,
                        columnWidth: '60%',
                        distributed: false,
                        colors: {
                            ranges: this.latestProfitsData.map(item => {
                                const value = item.profit || 0;
                                return {
                                    from: value < 0 ? value : 0,
                                    to: value < 0 ? 0 : value,
                                    color: value < 0 ? '#EF5350' :
                                        (this.$store.state.tournament?.tournamentSettings?.colors?.primary ?
                                            '#' + this.$store.state.tournament.tournamentSettings.colors.primary.replace('#', '') :
                                            'var(--v-mainGreen-base)')
                                };
                            })
                        }
                    }
                },
                dataLabels: {
                    enabled: false
                },
                tooltip: {
                    theme: this.isDarkTheme ? 'dark' : 'light',
                    style: {
                        fontSize: '12px',
                        fontFamily: 'Helvetica, Arial, sans-serif'
                    },
                    x: {
                        formatter: (val, opts) => {
                            const index = opts.dataPointIndex;
                            const item = this.latestProfitsData[index];
                            return item ? this.formatDateShort(item.date) : val;
                        }
                    },
                    y: {
                        formatter: function(val) {
                            return `$${val.toFixed(2)}`;
                        }
                    }
                },
                xaxis: {
                    categories: this.latestProfitsData.map(item => this.formatDateShort(item.date)),
                    title: {
                        text: this.$t('date') || 'Date',
                        style: {
                            color: this.isDarkTheme ? '#ffffff' : '#333333'
                        }
                    },
                    labels: {
                        style: {
                            colors: this.isDarkTheme ? '#ffffff' : '#333333'
                        },
                        rotate: -45,
                        rotateAlways: false
                    }
                },
                yaxis: {
                    title: {
                        text: this.$t('profit') || 'Profit ($)',
                        style: {
                            color: this.isDarkTheme ? '#ffffff' : '#333333'
                        }
                    },
                    labels: {
                        style: {
                            colors: this.isDarkTheme ? '#ffffff' : '#333333'
                        },
                        formatter: function(val) {
                            return `$${val.toFixed(2)}`;
                        }
                    }
                },
                grid: {
                    borderColor: this.isDarkTheme ? '#444' : '#e0e0e0',
                    strokeDashArray: 5,
                    xaxis: {
                        lines: {
                            show: true
                        }
                    },
                    yaxis: {
                        lines: {
                            show: true
                        }
                    }
                }
            };
        }
    },
    watch: {
        "$store.state.currentDetailedInfo": {
            immediate: true,
            handler(newVal) {
                this.updateDetailedInfo(newVal);
            }
        }
    },
    methods: {
        updateDetailedInfo(info) {
            // eslint-disable-next-line no-console
            console.log("info", info);
            if(info && info.latestTrades && info.latestTrades.length){
                this.indicators = [
                    { name: 'winRate', value: `${(info?.winRate * 100)?.toFixed(2)}%` },
                    { name: 'riskReward', value: info?.riskRewardRatio?.toFixed(2) },
                    { name: 'totalTrades', value: info?.totalTrades },
                ];
                this.trades = info.latestTrades.slice(0, 7);
                this.lotsByAsset = info.lotsByAsset || [];

                // Latest-profits series from backend (the cumulative PnL chart was
                // replaced by the ranking positions tab in SFB-1261)
                this.latestProfitsData = info.latestProfits || [];

                // Fallback: Use original chart data if new data not available
                if (!this.latestProfitsData.length) {
                    const reversedTrades = info.latestTrades.slice(0,7).reverse();
                    this.chartSeries[0].data = reversedTrades.map(trade => trade.profit);
                }
            }
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
        },
        formatDateShort(dateString) {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            return `${month}/${day}`;
        },
        getContrastYIQ(hexcolor) {
            if (!hexcolor) return 'black';
            
            hexcolor = hexcolor.replace('#', '');
            
            const r = parseInt(hexcolor.substr(0, 2), 16);
            const g = parseInt(hexcolor.substr(2, 2), 16);
            const b = parseInt(hexcolor.substr(4, 2), 16);
            
            const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
            
            return (yiq >= 128) ? 'black' : 'white';
        }
    }
};
</script>

<style scoped lang="scss">
.participant-detailed-info {
    width: 100%;
    background-color: var(--v-fullWhite-base);
    font-family: "Avenir", sans-serif;
    .no-content-txt{
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 32px !important;
        font-family: "Avenir", sans-serif !important;
        text-align: center;
        height: 250px;
        color: var(--v-mainBlack-base);
        opacity: 0.2;
        @media screen and (max-width: 768px) {
            font-size: 18px;
        }
    }
}

.tabs {
    display: flex;
    position: relative;
    background-color: var(--v-progressBarBackground-base);
    border-radius: 10px;
    width: 100%;
    margin-bottom: 10px;

    * {
        z-index: 2;
    }

    input[type="radio"] {
        display: none;

        &:checked {
            & + label {
                color: white !important;

                i {
                    color: white !important;
                }
            }
        }
    }

    .tab {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1;
        height: 38px;
        font-size: 14px;
        font-weight: 600;
        color: var(--v-mainBlack-base);
        border-radius: 10px;
        cursor: pointer;
        transition: color 0.15s ease-in;
        font-family: "Avenir", sans-serif;
        padding: 0 16px;
        gap: 8px;

        i {
            font-size: 14px;
            flex-shrink: 0;
        }

        .tab-label {
            line-height: 1.4;
        }

        // Mobile responsive
        @media screen and (max-width: 768px) {
            font-size: 12px;
            padding: 0 8px;
            gap: 6px;

            i {
                font-size: 12px;
            }

            .tab-label {
                display: none;
            }
        }

        @media screen and (max-width: 480px) {
            font-size: 11px;
            padding: 0 6px;

            i {
                font-size: 16px;
            }
        }
    }

    .glider {
        position: absolute;
        display: flex;
        height: 38px;
        width: calc(100% / 3); // For 3 tabs
        border-radius: 10px;
        z-index: 1;
        transition: 0.25s ease-out;
    }
}

.theme--dark .tabs {
    background-color: #303740;

    .tab {
        color: var(--v-fullWhite-base);

        &:hover {
            opacity: 0.8;
        }
    }
}


// SFB-1261 — latest ranking positions
.ranking-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 6px 0 2px;
}

.ranking-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
    width: 100%;
    margin-bottom: 16px;
}

.ranking-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 16px 12px;
    border: 1px solid var(--v-progressBarBackground-base);
    border-radius: 12px;
    background-color: var(--v-fullWhite-base);
    text-align: center;
    min-height: 118px;

    &__label {
        margin: 0;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        color: var(--v-mainBlack-base);
        opacity: 0.6;
    }

    &__value {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        min-height: 40px;

        i {
            font-size: 22px;
        }
    }

    &__medal {
        width: 28px;
        height: 28px;
    }

    &__number {
        font-size: 32px;
        font-weight: 800;
        line-height: 1;
        color: var(--v-mainBlack-base);

        &--empty {
            opacity: 0.3;
        }

        &--score {
            font-size: 22px;
        }
    }

    &__hint {
        margin: 0;
        font-size: 12px;
        color: var(--v-mainBlack-base);
        opacity: 0.6;
    }

    &--current.gold { border-color: #f5c542; }
    &--current.silver { border-color: #bfc6cf; }
    &--current.bronze { border-color: #d59a63; }

    &--up {
        .ranking-card__number,
        i { color: var(--v-mainGreen-base); }
    }

    &--down {
        .ranking-card__number,
        i { color: #EF5350; }
    }

    @media screen and (max-width: 768px) {
        min-height: 100px;
        padding: 12px 8px;

        &__number {
            font-size: 26px;
        }
    }
}

.theme--dark .ranking-card {
    background-color: #1f242b;
    border-color: #303740;
}

.participant-data-disclaimer {
    font-size: 14px;
    line-height: 1.4;
    color: #14161B;
    text-align: center;
    margin: 0;
}

.indicators {
    display: flex;
    justify-content: space-around;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin: 4px 0 16px;
    width: 100%;
}

.indicator {
    display: flex;
    justify-content: space-between;
    color: #fff;
    padding: 10px;
    align-items: center;
    border-radius: 10px;
    border: 1px solid var(--v-mainBlack-base);
    text-align: center;
    font-size: 12px;

    .indicator-value {
        font-weight: bold;
        margin-left: 40px;
        font-size: 14px;
    }
}

.trades-content {
    width: 100%;

    table {
        width: 100%;
        border-collapse: collapse;

        th,
        td {
            padding: 20px 10px;
            text-align: center;
            border-bottom: 1px solid #eee;
        }

        th {
            background-color: var(--v-progressBarBackground-base);
            ;
            border-bottom: none;
        }

        .first-header {
            border-top-left-radius: 10px;
            border-bottom-left-radius: 10px;
        }

        .last-header {
            border-top-right-radius: 10px;
            border-bottom-right-radius: 10px;
        }

        .no-data-message {
            padding: 40px 20px;
            color: var(--v-mainBlack-base);
            opacity: 0.6;
            font-style: italic;
        }
    }
}


.lots-by-asset-content {
    width: 100%;

    table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;

        th,
        td {
            padding: 20px 10px;
            text-align: center;
            border-bottom: 1px solid #eee;
        }

        th {
            background-color: var(--v-progressBarBackground-base);
            border-bottom: none;
        }

        th:first-child,
        td:first-child {
            width: 50%;
        }

        th:last-child,
        td:last-child {
            width: 50%;
        }

        tbody tr {
            transition: background-color 0.2s ease;

            &:nth-child(even) {
                background-color: #f9f9f9;
            }

            &:hover {
                background-color: #f0f0f0;
            }
        }

        .theme--dark & tbody tr {
            &:nth-child(even) {
                background-color: rgba(255, 255, 255, 0.03);
            }

            &:hover {
                background-color: rgba(255, 255, 255, 0.08);
            }
        }

        .first-header {
            border-top-left-radius: 10px;
            border-bottom-left-radius: 10px;
        }

        .last-header {
            border-top-right-radius: 10px;
            border-bottom-right-radius: 10px;
        }

        .no-data-message {
            padding: 40px 20px;
            color: var(--v-mainBlack-base);
            opacity: 0.6;
            font-style: italic;
        }
    }
}

@media screen and (max-width: 768px) {
    .tabs {
        flex-direction: column;
        align-items: stretch;
    }

    .tabs-container {
        flex-direction: column;
    }

    .tabs button {
        width: 100%;
    }
}

/* Slide effect for tab transition */
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.3s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
    transform: translateX(10px);
    opacity: 0;
}
::v-deep .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-title {
   background-color: transparent;
}

::v-deep .apexcharts-xaxistooltip.apexcharts-theme-dark{
    background-color: transparent;
}
</style>
